import React from 'react';
import { CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { SeoReport } from '../services/aiService';
import { t } from '../services/i18n';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';

interface ProgressiveAuditDisplayProps {
  report: SeoReport | null;
  partialReport: SeoReport | null;
  auditProgress: Map<string, any>;
  isLoading: boolean;
}

interface AuditSection {
  id: string;
  title: string;
  key: string;
  isComplete: (report: SeoReport | null, partial: SeoReport | null, progress: Map<string, any>) => boolean;
  render: (report: SeoReport | null, partial: SeoReport | null) => React.ReactNode;
}

const ProgressiveAuditDisplay: React.FC<ProgressiveAuditDisplayProps> = ({
  report,
  partialReport,
  auditProgress,
  isLoading
}) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  
  const currentReport = report || partialReport;
  
  // Define all audit sections
  const sections: AuditSection[] = [
    {
      id: 'overview',
      title: t('auditProgress.overviewScore') || 'Overview & Score',
      key: 'overview',
      isComplete: (r, p) => !!(r || p)?.overallScore,
      render: (r, p) => {
        const data = r || p;
        if (!data) return null;
        
        // Show instant metrics if available
        const instantMetrics = (data as any).instantMetrics;
        
        return (
          <div className="professional-card bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6 animate-fadeInUp">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold gradient-text animate-slideInLeft">Overall SEO Score</h3>
              <div className="text-4xl font-bold text-indigo-400 animate-bounceIn">{data.overallScore || 0}/100</div>
            </div>
            
            {/* Show instant metrics if available */}
            {instantMetrics && (
              <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-xs font-semibold text-green-400">Instant Extraction ({instantMetrics.responseTime})</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400">Title:</span>
                    <span className="text-white ml-1">{instantMetrics.title?.substring(0, 30) || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">H1:</span>
                    <span className="text-white ml-1">{instantMetrics.h1Count || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Images:</span>
                    <span className="text-white ml-1">{instantMetrics.images?.total || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Links:</span>
                    <span className="text-white ml-1">{instantMetrics.links?.total || 0}</span>
                  </div>
                </div>
              </div>
            )}
            
            {data.summary && (
              <p className="text-slate-300 text-sm leading-relaxed">{data.summary}</p>
            )}
            {data.categories && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {Object.entries(data.categories).map(([category, catData]: [string, any]) => (
                  <div key={category} className="bg-slate-900/50 rounded-lg p-3">
                    <div className="text-xs text-slate-400 mb-1">{category}</div>
                    <div className="text-2xl font-bold text-white">{catData.score || 0}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {catData.totalIssues || 0} issues
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'onpage',
      title: t('results.categoryOnPage') || 'On-Page SEO',
      key: 'onPage',
      isComplete: (r, p) => {
        const data = r || p;
        return !!(data?.categories?.['On-Page SEO'] || data?.categories?.['SEO ON-PAGE'] || data?.categories?.['ON-PAGE SEO']);
      },
      render: (r, p) => {
        const data = r || p;
        if (!data) return null;
        // Match multiple category name variations
        const issues = data.issues?.filter((i: any) => {
          const cat = i.category?.toLowerCase() || '';
          return cat.includes('on-page') || cat.includes('onpage') || cat === 'on-page seo';
        }) || [];
        
        // If no filtered issues but we have issues, show first few issues anyway
        const displayIssues = issues.length > 0 ? issues : (data.issues?.slice(0, 3) || []);
        
        return (
          <div className="space-y-3">
            {displayIssues.slice(0, 5).map((issue: any, idx: number) => (
              <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    issue.severity === 'Critical' ? 'bg-red-500' :
                    issue.severity === 'High' ? 'bg-orange-500' :
                    issue.severity === 'Medium' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-white text-sm mb-1">{issue.title}</h4>
                    <p className="text-slate-400 text-xs mb-2">{issue.description}</p>
                    {issue.fix && (
                      <div className="bg-slate-900/50 rounded p-2 mt-2">
                        <p className="text-green-400 text-xs font-semibold mb-1">Fix:</p>
                        <p className="text-slate-300 text-xs">{issue.fix}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {displayIssues.length === 0 && issues.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>{t('results.noOnPageIssues') || 'No on-page SEO issues found!'}</p>
              </div>
            )}
            {displayIssues.length > 0 && issues.length === 0 && (
              <div className="text-center py-4 text-slate-400 text-xs">
                <p>Showing general issues (category-specific filtering may not match)</p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'technical',
      title: 'Technical SEO',
      key: 'technical',
      isComplete: (r, p) => {
        const data = r || p;
        return !!(data?.categories?.['Technical SEO'] || data?.categories?.['SEO TECHNIQUE'] || data?.categories?.['TECHNICAL SEO']);
      },
      render: (r, p) => {
        const data = r || p;
        if (!data) return null;
        // Match multiple category name variations
        const issues = data.issues?.filter((i: any) => {
          const cat = i.category?.toLowerCase() || '';
          return cat.includes('technical') || cat === 'technical seo';
        }) || [];
        
        // If no filtered issues but we have issues, show first few issues anyway
        const displayIssues = issues.length > 0 ? issues : (data.issues?.slice(0, 3) || []);
        
        return (
          <div className="space-y-3">
            {displayIssues.slice(0, 5).map((issue: any, idx: number) => (
              <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    issue.severity === 'Critical' ? 'bg-red-500' :
                    issue.severity === 'High' ? 'bg-orange-500' :
                    issue.severity === 'Medium' ? 'bg-yellow-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-white text-sm mb-1">{issue.title}</h4>
                    <p className="text-slate-400 text-xs mb-2">{issue.description}</p>
                    {issue.fix && (
                      <div className="bg-slate-900/50 rounded p-2 mt-2">
                        <p className="text-green-400 text-xs font-semibold mb-1">Fix:</p>
                        <p className="text-slate-300 text-xs">{issue.fix}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {displayIssues.length === 0 && issues.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>{t('results.noTechnicalIssues') || 'No technical SEO issues found!'}</p>
              </div>
            )}
            {displayIssues.length > 0 && issues.length === 0 && (
              <div className="text-center py-4 text-slate-400 text-xs">
                <p>Showing general issues (category-specific filtering may not match)</p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'broken-links',
      title: t('results.brokenLinks') || 'Broken Links',
      key: 'brokenLinks',
      isComplete: (r, p) => {
        const data = r || p;
        return !!(data?.linkAnalysis?.brokenLinks);
      },
      render: (r, p) => {
        const data = r || p;
        if (!data?.linkAnalysis?.brokenLinks) return null;
        const brokenLinks = data.linkAnalysis.brokenLinks || [];
        return (
          <div className="space-y-3">
            {brokenLinks.length > 0 ? (
              brokenLinks.map((link: any, idx: number) => (
                <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-sm mb-1">
                        {link.url?.substring(0, 60)}{link.url?.length > 60 ? '...' : ''}
                      </h4>
                      <p className="text-slate-400 text-xs mb-1">
                        Status: {link.statusCode || 404} - {link.statusText || 'Not Found'}
                      </p>
                      {link.anchorText && (
                        <p className="text-slate-500 text-xs">Anchor: "{link.anchorText}"</p>
                      )}
                      {link.foundIn && (
                        <p className="text-slate-500 text-xs">Found on: {link.foundIn}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No broken links found!</p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'redirects',
      title: t('results.redirects') || 'Redirects (301/302)',
      key: 'redirects',
      isComplete: (r, p) => {
        const data = r || p;
        return !!(data?.linkAnalysis?.redirects);
      },
      render: (r, p) => {
        const data = r || p;
        if (!data?.linkAnalysis?.redirects) return null;
        const redirects = data.linkAnalysis.redirects || [];
        return (
          <div className="space-y-3">
            {redirects.length > 0 ? (
              redirects.map((redirect: any, idx: number) => (
                <div key={idx} className={`border rounded-lg p-4 ${
                  redirect.isCanonical 
                    ? 'bg-blue-500/10 border-blue-500/20' 
                    : 'bg-yellow-500/10 border-yellow-500/20'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                      redirect.isCanonical ? 'bg-blue-500' : 'bg-yellow-500'
                    }`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-sm mb-1">
                        {redirect.redirectType || 'Redirect'} ({redirect.statusCode || 301})
                      </h4>
                      <p className="text-slate-400 text-xs mb-1">
                        From: {redirect.from?.substring(0, 50)}{redirect.from?.length > 50 ? '...' : ''}
                      </p>
                      <p className="text-slate-400 text-xs">
                        To: {redirect.to?.substring(0, 50)}{redirect.to?.length > 50 ? '...' : ''}
                      </p>
                      {redirect.isCanonical && (
                        <span className="inline-block mt-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                          Canonical Redirect
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No redirects detected!</p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'minification',
      title: t('results.assetMinification') || 'Asset Minification (CSS/JS)',
      key: 'minification',
      isComplete: (r, p) => {
        const data = r || p;
        return !!(data?.performanceAudit?.minification);
      },
      render: (r, p) => {
        const data = r || p;
        const min = data?.performanceAudit?.minification;
        
        // Show loading state if performance audit is in progress
        if (!min && isLoading) {
          return (
            <div className="text-center py-8 text-slate-500">
              <Loader2 className="w-8 h-8 mx-auto mb-2 text-indigo-400 animate-spin" />
              <p>{t('auditProgress.analyzingMinification') || 'Analyzing asset minification...'}</p>
            </div>
          );
        }
        
        if (!min) {
          return (
            <div className="text-center py-8 text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 text-yellow-500" />
              <p>Minification analysis not available</p>
              <p className="text-xs mt-2">This may take a few moments to complete...</p>
            </div>
          );
        }
        
        const unminifiedCSS = min.css?.filter((f: any) => !f.isMinified) || [];
        const unminifiedJS = min.js?.filter((f: any) => !f.isMinified) || [];
        const totalCSS = min.css?.length || 0;
        const totalJS = min.js?.length || 0;
        const minifiedCSS = totalCSS - unminifiedCSS.length;
        const minifiedJS = totalJS - unminifiedJS.length;
        const totalSavings = min.totalSavingsFormatted || '0 KB';
        
        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-slate-400 text-xs mb-1">CSS Files</div>
                  <div className="text-white font-semibold">{totalCSS} total</div>
                  <div className="text-green-400 text-xs">{minifiedCSS} minified</div>
                  {unminifiedCSS.length > 0 && (
                    <div className="text-yellow-400 text-xs">{unminifiedCSS.length} unminified</div>
                  )}
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">JS Files</div>
                  <div className="text-white font-semibold">{totalJS} total</div>
                  <div className="text-green-400 text-xs">{minifiedJS} minified</div>
                  {unminifiedJS.length > 0 && (
                    <div className="text-yellow-400 text-xs">{unminifiedJS.length} unminified</div>
                  )}
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">Potential Savings</div>
                  <div className="text-indigo-400 font-semibold">{totalSavings}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">Status</div>
                  {unminifiedCSS.length === 0 && unminifiedJS.length === 0 ? (
                    <div className="text-green-400 font-semibold">✓ All Minified</div>
                  ) : (
                    <div className="text-yellow-400 font-semibold">⚠ Needs Optimization</div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Unminified CSS */}
            {unminifiedCSS.length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  Unminified CSS ({unminifiedCSS.length})
                </h5>
                <div className="space-y-2">
                  {unminifiedCSS.slice(0, 5).map((file: any, idx: number) => (
                    <div key={idx} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                      <p className="text-white text-xs font-mono mb-1 break-all">
                        {file.url || 'Unknown URL'}
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-slate-400">Size: <span className="text-white">{file.originalSize || 'N/A'}</span></span>
                        {file.potentialSavingsFormatted && (
                          <span className="text-green-400">Potential savings: {file.potentialSavingsFormatted}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {unminifiedCSS.length > 5 && (
                    <p className="text-slate-500 text-xs text-center">
                      +{unminifiedCSS.length - 5} more unminified CSS files
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Unminified JS */}
            {unminifiedJS.length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  Unminified JavaScript ({unminifiedJS.length})
                </h5>
                <div className="space-y-2">
                  {unminifiedJS.slice(0, 5).map((file: any, idx: number) => (
                    <div key={idx} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                      <p className="text-white text-xs font-mono mb-1 break-all">
                        {file.url || 'Unknown URL'}
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-slate-400">Size: <span className="text-white">{file.originalSize || 'N/A'}</span></span>
                        {file.potentialSavingsFormatted && (
                          <span className="text-green-400">Potential savings: {file.potentialSavingsFormatted}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {unminifiedJS.length > 5 && (
                    <p className="text-slate-500 text-xs text-center">
                      +{unminifiedJS.length - 5} more unminified JS files
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* All minified */}
            {unminifiedCSS.length === 0 && unminifiedJS.length === 0 && totalCSS + totalJS > 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p className="text-green-400 font-semibold">All assets are minified!</p>
                <p className="text-xs mt-1">Great job optimizing your assets.</p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'issues',
      title: t('results.allIssues') || 'All Issues',
      key: 'issues',
      isComplete: (r, p) => !!(r || p)?.issues,
      render: (r, p) => {
        const data = r || p;
        if (!data) return null;
        const issues = data.issues || [];
        
        if (issues.length === 0) {
          return (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>{t('results.noIssuesFound') || 'No issues found! Your website is in great shape.'}</p>
            </div>
          );
        }
        
        return (
          <div className="space-y-3">
            {issues.map((issue: any, idx: number) => (
              <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    issue.severity === 'Critical' ? 'bg-red-500' :
                    issue.severity === 'High' ? 'bg-orange-500' :
                    issue.severity === 'Medium' ? 'bg-yellow-500' :
                    issue.severity === 'Low' ? 'bg-blue-500' : 'bg-slate-500'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-white text-sm">{issue.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        issue.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                        issue.severity === 'High' ? 'bg-orange-500/20 text-orange-400' :
                        issue.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        issue.severity === 'Low' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {issue.severity}
                      </span>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{issue.description}</p>
                    {issue.fix && (
                      <div className="bg-slate-900/50 rounded p-2 mt-2">
                        <p className="text-green-400 text-xs font-semibold mb-1">Fix:</p>
                        <p className="text-slate-300 text-xs">{issue.fix}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      {sections.map((section, index) => {
        const isComplete = section.isComplete(report, partialReport, auditProgress);
        
        // Get step progress to check status
        const stepKey = section.key === 'onPage' ? 'enhanced' : 
                       section.key === 'technical' ? 'enhanced' :
                       section.key === 'brokenLinks' || section.key === 'redirects' ? 'link-performance' :
                       section.key === 'minification' ? 'link-performance' : 'ai-analysis';
        const stepProgress = auditProgress.get(stepKey);
        
        // Handle all status types
        const getStepStatus = () => {
          if (isComplete) return 'completed';
          if (stepProgress?.status === 'completed') return 'completed';
          if (stepProgress?.status === 'running') return 'running';
          if (stepProgress?.status === 'timeout' || stepProgress?.status === 'failed') return 'warning';
          if (stepProgress?.status === 'skipped') return 'skipped';
          if (isLoading) return 'running';
          return 'pending';
        };
        
        const stepStatus = getStepStatus();
        const isInProgress = stepStatus === 'running';
        const isWarning = stepStatus === 'warning';
        const hasData = section.render(currentReport, partialReport) !== null;

        return (
          <div
            key={section.id}
            className={`stagger-item relative professional-card bg-slate-800/50 border rounded-xl p-6 transition-all duration-300 ${
              isComplete
                ? 'border-slate-700 opacity-100 animate-fadeInUp'
                : isInProgress
                ? 'border-indigo-500/50 opacity-75'
                : 'border-slate-700/50 opacity-50'
            }`}
          >
            {/* Loading overlay for incomplete sections - Enhanced */}
            {isInProgress && (
              <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10 animate-fadeIn">
                <div className="text-center animate-bounceIn">
                  <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-2" />
                  <p className="text-slate-400 text-sm animate-pulse">{t('auditProgress.processing') || 'Processing'} {section.title}...</p>
                  <div className="mt-4 w-48 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-progress-fill animate-shimmer"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Section header - Enhanced */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 animate-slideInLeft">
                {stepStatus === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-green-400 animate-bounceIn" />
                ) : stepStatus === 'running' ? (
                  <Clock className="w-5 h-5 text-indigo-400 animate-pulse animate-rotate-in" />
                ) : stepStatus === 'warning' ? (
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                ) : (
                  <div className="w-5 h-5 border-2 border-slate-600 rounded-full animate-pulse" />
                )}
                <span className="gradient-text">{section.title}</span>
              </h3>
              <div className="flex items-center gap-3">
                {/* Show progress percentage for in-progress sections */}
                {stepStatus === 'running' && (() => {
                  const progressPercent = stepProgress?.progress || 0;
                  const estimatedTime = stepProgress?.estimatedTimeRemaining 
                    ? Math.ceil(stepProgress.estimatedTimeRemaining / 1000) 
                    : null;
                  
                  return (
                    <>
                      {estimatedTime && (
                        <span className="text-xs text-indigo-400 font-semibold animate-pulse">
                          ⏱️ ~{estimatedTime}s
                        </span>
                      )}
                      <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded animate-pulse">
                        {progressPercent > 0 ? `${Math.round(progressPercent)}%` : (t('auditProgress.processing') || 'Processing...')}
                      </span>
                    </>
                  );
                })()}
                {stepStatus === 'completed' && (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                    {t('auditProgress.complete') || 'Complete'}
                  </span>
                )}
                {stepStatus === 'warning' && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                    {stepProgress?.message || 'Unavailable'}
                  </span>
                )}
              </div>
            </div>
            
            {/* Progress bar for in-progress sections */}
            {isInProgress && (() => {
              const stepProgress = auditProgress.get(section.key === 'onPage' ? 'enhanced' : 
                                                     section.key === 'technical' ? 'enhanced' :
                                                     section.key === 'brokenLinks' || section.key === 'redirects' ? 'link-performance' :
                                                     section.key === 'minification' ? 'link-performance' : 'ai-analysis');
              const progressPercent = stepProgress?.progress || 0;
              
              if (progressPercent > 0) {
                return (
                  <div className="w-full bg-slate-700/50 rounded-full h-2 mb-4 overflow-hidden shadow-inner animate-fadeInUp">
                    <div 
                      className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500 ease-out relative overflow-hidden animate-progress-fill"
                      style={{ width: `${progressPercent}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Section content */}
            <div className={isInProgress ? 'opacity-30' : ''}>
              {hasData ? (
                section.render(currentReport, partialReport)
              ) : (
                <div className="text-center py-8 text-slate-500">
                  {isInProgress ? (
                    <>
                      <Loader2 className="w-8 h-8 mx-auto mb-2 text-indigo-400 animate-spin" />
                      <p>{t('auditProgress.analyzingWebsite') || 'Analyzing'} {section.title.toLowerCase()}...</p>
                    </>
                  ) : (
                    <p>No data available yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgressiveAuditDisplay;

